import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inheritsLoose from "@babel/runtime/helpers/inheritsLoose";
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { XBoldIcon } from '@pattern-library/icon';
import throttle from 'lodash/throttle';
var propTypes = {
  /**
   * Custom classes to apply to the message popover containing div
   */
  className: PropTypes.string,
  /**
   * The target element that the message popover will point to.
   * <br />Accepts the ID string of the DOM element, or a ref object.
   **/
  target: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Vertical distance from target element
   **/
  verticalOffset: PropTypes.number,
  /**
   * Horizontal distance from target element (not available for center horizontal positioning)
   **/
  horizontalOffset: PropTypes.number,
  /**
   * Identifier for automation tests
   **/
  qaId: PropTypes.string,
  /**
   * Callback function for message popover close event
   **/
  onClose: PropTypes.func,
  /**
   * Message popover visibility state
   **/
  isOpen: PropTypes.bool,
  /**
   * Position of arrow and message popover relative to target element (center horizontal position ignores horizontalOffset)
   **/
  arrowPosition: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'center', 'right'])
  }),
  /**
   * Content that appears in the body of the popover
   **/
  children: PropTypes.node,
  /**
   * If enabled, close message popover when user clicks outside of the containing div
   **/
  closeOnOutsideClick: PropTypes.bool,
  /**
   * max-width style applied to message popover containing div.
   * <br />Must be a valid CSS unit value.
   */
  maxWidth: PropTypes.string,
  /**
   * Flag to check if the close button needs to be displayed
   **/
  useCloseButton: PropTypes.bool,
  /**
   * Use fixed positioning to place the popover
   */
  useFixedPositioning: PropTypes.bool,
  /**
   * Title for close button to help screen readers
   **/
  closeButtonTitle: PropTypes.string,
  /**
   * ID for the message popover content div
   */
  id: PropTypes.string,
  /**
   * Automatically reposition the popover if it overflows the window
   */
  useResponsivePositioning: PropTypes.bool
};
var defaultProps = {
  verticalOffset: 16,
  horizontalOffset: 40,
  onClose: function onClose() {},
  qaId: '',
  target: '',
  isOpen: true,
  arrowPosition: {
    vertical: 'bottom',
    horizontal: 'left'
  },
  children: [],
  closeOnOutsideClick: false,
  useCloseButton: false,
  useFixedPositioning: true,
  closeButtonTitle: 'Close message',
  useResponsivePositioning: false
};
var MessagePopover = /*#__PURE__*/function (_React$Component) {
  _inheritsLoose(MessagePopover, _React$Component);
  function MessagePopover(props) {
    var _this;
    _this = _React$Component.call(this, props) || this;
    _this.initState = function () {
      return {
        top: 0,
        left: 0,
        popoverHeight: 0,
        popoverWidth: 0,
        targetWidth: 0,
        arrowPosition: _objectSpread({}, _this.props.arrowPosition)
      };
    };
    _this.addEventListeners = function () {
      var _this$props = _this.props,
        closeOnOutsideClick = _this$props.closeOnOutsideClick,
        useFixedPositioning = _this$props.useFixedPositioning;
      if (closeOnOutsideClick) {
        document.addEventListener('mousedown', _this.handleClickOutside);
      }
      if (useFixedPositioning) {
        window.addEventListener('resize', _this.handleWindowChanges);
        window.addEventListener('scroll', _this.handleWindowChanges);
      }
    };
    _this.removeEventListeners = function () {
      var _this$props2 = _this.props,
        closeOnOutsideClick = _this$props2.closeOnOutsideClick,
        useFixedPositioning = _this$props2.useFixedPositioning;
      if (closeOnOutsideClick) {
        document.removeEventListener('mousedown', _this.handleClickOutside);
      }
      if (useFixedPositioning) {
        _this.handleWindowChanges.cancel();
        window.removeEventListener('resize', _this.handleWindowChanges);
        window.removeEventListener('scroll', _this.handleWindowChanges);
      }
    };
    _this.positionPopup = function () {
      var targetOffset = _this.getTargetOffset();
      var arrowPosition = _this.state.arrowPosition;
      var _this$popoverRef$getB = _this.popoverRef.getBoundingClientRect(),
        width = _this$popoverRef$getB.width,
        height = _this$popoverRef$getB.height;
      var top = arrowPosition.vertical === 'bottom' ? _this.positionPopopAbove(targetOffset, height) : _this.positionPopopBelow(targetOffset);
      var left = arrowPosition.horizontal === 'left' ? _this.setLeftAligned(targetOffset) : _this.props.arrowPosition.horizontal === 'center' ? _this.setCenterAligned(targetOffset, width) : _this.setRightAligned(targetOffset, width);
      _this.setState({
        top: top,
        left: left
      }, function () {
        return _this.checkVisibility(true);
      });
    };
    _this.checkVisibility = function (isFirstPass) {
      if (_this.props.useResponsivePositioning) {
        var targetOffset = _this.getTargetOffset();
        var currentArrowPosition = _this.state.arrowPosition;
        var _this$popoverRef$getB2 = _this.popoverRef.getBoundingClientRect(),
          top = _this$popoverRef$getB2.top,
          left = _this$popoverRef$getB2.left,
          right = _this$popoverRef$getB2.right,
          bottom = _this$popoverRef$getB2.bottom,
          width = _this$popoverRef$getB2.width,
          height = _this$popoverRef$getB2.height;
        var windowHeight = window.outerHeight;
        var windowWidth = window.outerWidth;
        var newTop = _this.state.top;
        if (top < 0 && height <= windowHeight && currentArrowPosition.vertical === 'bottom') {
          newTop = _this.positionPopopBelow(targetOffset);
          currentArrowPosition.vertical = 'top';
        } else if (bottom > windowHeight && currentArrowPosition.vertical === 'top') {
          newTop = _this.positionPopopAbove(targetOffset, height);
          currentArrowPosition.vertical = 'bottom';
        }
        var newLeft = _this.state.left;
        if (width > windowWidth) {
          newLeft = _this.setCenterAligned(targetOffset, width);
          currentArrowPosition.horizontal = 'center';
        } else if (left < 0 && currentArrowPosition.horizontal === 'right') {
          newLeft = _this.setLeftAligned(targetOffset);
          currentArrowPosition.horizontal = 'left';
        } else if (right > windowWidth && currentArrowPosition.horizontal === 'left') {
          newLeft = _this.setRightAligned(targetOffset, width);
          currentArrowPosition.horizontal = 'right';
        }
        _this.setState({
          top: newTop,
          left: newLeft,
          arrowPosition: currentArrowPosition
        }, function () {
          return isFirstPass && _this.checkVisibility(false);
        });
      }
    };
    _this.resetState = function () {
      _this.setState({
        arrowPosition: _objectSpread({}, _this.props.arrowPosition)
      });
    };
    _this.openPopover = function () {
      _this.resetState();
      _this.updatePopoverDimensions();
      _this.addEventListeners();
    };
    _this.closePopover = function () {
      _this.removeEventListeners();
    };
    _this.handleClickOutside = function (event) {
      var _this$props3 = _this.props,
        isOpen = _this$props3.isOpen,
        onClose = _this$props3.onClose;
      var isElementInsidePopover = _this.popoverRef.contains(event.target);
      var targetElement = _this.getTargetElement();
      var isElementInsideCloseButton = targetElement.contains(event.target);

      // If external close button is clicked, toggle it to prevent state mishaps
      // If the popover or an element within it is clicked, prevent closing
      if (isElementInsideCloseButton || isElementInsidePopover) return;
      if (isOpen && _this.popoverRef) {
        onClose();
      }
    };
    _this.getArrowStyles = function (arrowHorizontalPositon) {
      var horizontalOffset = _this.props.horizontalOffset;
      var targetWidth = _this.state.targetWidth;
      var arrowWidth = 26; // .asc-message-popover--arrow: border-width * 2
      var arrowOffsetMargin = horizontalOffset + targetWidth / 2 - arrowWidth / 2 - 1;
      var arrowStyles = {};
      switch (arrowHorizontalPositon) {
        case 'left':
          arrowStyles = {
            marginLeft: arrowOffsetMargin
          };
          break;
        case 'center':
          arrowStyles = {
            marginLeft: arrowWidth / -2
          };
          break;
        case 'right':
          arrowStyles = {
            marginRight: arrowOffsetMargin
          };
          break;
        default:
          break;
      }
      return arrowStyles;
    };
    _this.state = _this.initState();
    _this.handleWindowChanges = throttle(_this.positionPopup.bind(_assertThisInitialized(_this)), 10);
    return _this;
  }
  var _proto = MessagePopover.prototype;
  _proto.componentDidMount = function componentDidMount() {
    var isOpen = this.props.isOpen;
    this.updatePopoverDimensions();
    if (isOpen) {
      this.openPopover();
    }
  };
  _proto.componentWillUnmount = function componentWillUnmount() {
    this.removeEventListeners();
  };
  _proto.componentDidUpdate = function componentDidUpdate(prevProps) {
    var _this$props4 = this.props,
      horizontalOffset = _this$props4.horizontalOffset,
      isOpen = _this$props4.isOpen,
      useFixedPositioning = _this$props4.useFixedPositioning,
      verticalOffset = _this$props4.verticalOffset;
    if (prevProps.horizontalOffset !== horizontalOffset || prevProps.verticalOffset !== verticalOffset || prevProps.useFixedPositioning !== useFixedPositioning) {
      this.updatePopoverDimensions();
    }
    if (isOpen && !prevProps.isOpen) {
      this.openPopover();
    } else if (!isOpen && prevProps.isOpen) {
      this.closePopover();
    }
  };
  _proto.updatePopoverDimensions = function updatePopoverDimensions() {
    var target = this.getTargetElement();
    if (target === null) return;
    var _this$popoverRef$getB3 = this.popoverRef.getBoundingClientRect(),
      popoverWidth = _this$popoverRef$getB3.width,
      popoverHeight = _this$popoverRef$getB3.height;
    var _target$getBoundingCl = target.getBoundingClientRect(),
      targetWidth = _target$getBoundingCl.width;
    this.setState({
      popoverWidth: popoverWidth,
      popoverHeight: popoverHeight,
      targetWidth: targetWidth
    }, this.positionPopup);
  };
  _proto.getTargetElement = function getTargetElement() {
    var target = this.props.target;
    if (typeof target === 'string') {
      return document.getElementById(target);
    }
    return (target === null || target === void 0 ? void 0 : target.current) || null;
  };
  _proto.positionPopopAbove = function positionPopopAbove(_ref, popoverHeight) {
    var top = _ref.top;
    var verticalOffset = this.props.verticalOffset;
    return Math.floor(top - (popoverHeight + verticalOffset));
  };
  _proto.positionPopopBelow = function positionPopopBelow(_ref2) {
    var bottom = _ref2.bottom;
    var verticalOffset = this.props.verticalOffset;
    return Math.ceil(bottom + verticalOffset);
  };
  _proto.setLeftAligned = function setLeftAligned(_ref3) {
    var left = _ref3.left;
    var horizontalOffset = this.props.horizontalOffset;
    return Math.floor(left - horizontalOffset);
  };
  _proto.setCenterAligned = function setCenterAligned(_ref4, popoverWidth) {
    var left = _ref4.left;
    var targetWidth = this.state.targetWidth;
    return Math.floor(left + targetWidth / 2 - popoverWidth / 2);
  };
  _proto.setRightAligned = function setRightAligned(_ref5, popoverWidth) {
    var right = _ref5.right;
    var horizontalOffset = this.props.horizontalOffset;
    return Math.ceil(right - popoverWidth + horizontalOffset);
  };
  _proto.getTargetOffset = function getTargetOffset() {
    var useFixedPositioning = this.props.useFixedPositioning;
    var target = this.getTargetElement();
    var _target$getBoundingCl2 = target.getBoundingClientRect(),
      top = _target$getBoundingCl2.top,
      left = _target$getBoundingCl2.left,
      right = _target$getBoundingCl2.right,
      bottom = _target$getBoundingCl2.bottom,
      width = _target$getBoundingCl2.width,
      height = _target$getBoundingCl2.height;
    if (useFixedPositioning) {
      return {
        top: top,
        left: left,
        right: right,
        bottom: bottom
      };
    }
    return {
      top: 0,
      left: 0,
      right: width,
      bottom: height
    };
  };
  _proto.render = function render() {
    var _this2 = this;
    var _this$props5 = this.props,
      className = _this$props5.className,
      qaId = _this$props5.qaId,
      id = _this$props5.id,
      onClose = _this$props5.onClose,
      isOpen = _this$props5.isOpen,
      maxWidth = _this$props5.maxWidth,
      useCloseButton = _this$props5.useCloseButton,
      useFixedPositioning = _this$props5.useFixedPositioning,
      closeButtonTitle = _this$props5.closeButtonTitle,
      children = _this$props5.children;
    var _this$state = this.state,
      arrowPosition = _this$state.arrowPosition,
      top = _this$state.top,
      left = _this$state.left;
    var popoverInlineStyles = {
      maxWidth: maxWidth,
      top: top,
      left: left
    };
    var classString = classNames(className, 'asc-message-popover', "arrow-".concat(arrowPosition.vertical, "-").concat(arrowPosition.horizontal), {
      'is-hidden': !isOpen,
      'asc-message-popover--fixed': useFixedPositioning
    });
    var arrowStyles = this.getArrowStyles(arrowPosition.horizontal);
    return /*#__PURE__*/React.createElement("div", {
      className: classString,
      "data-qa": qaId,
      style: popoverInlineStyles,
      ref: function ref(node) {
        _this2.popoverRef = node;
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "asc-message-popover--arrow",
      style: arrowStyles
    }), useCloseButton && /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: "asc-message-popover--xicon",
      onClick: onClose,
      title: closeButtonTitle
    }, /*#__PURE__*/React.createElement(XBoldIcon, {
      size: "xxs"
    })), /*#__PURE__*/React.createElement("div", {
      id: id
    }, children));
  };
  return MessagePopover;
}(React.Component);
MessagePopover.propTypes = propTypes;
MessagePopover.defaultProps = defaultProps;
export default MessagePopover;